<template>
  <Card class="card px-1 border-surface-200 dark:border-surface-600 border dark:bg-surface-800! h-fit w-full my-auto" pt:content:class="p-0!">
    <template #content>
      <div class="flex justify-evenly items-center gap-2">
        <div>
          <span class="text-sm">Loan To Pay</span>
          <h1 class="font-bold text-5xl mb-3">{{ formatCurrency(availableLoan - totalLoan) }}</h1>
        </div>
        <div>
          <span class="text-sm">Equity To Pay</span>
          <h1 class="font-bold text-5xl mb-3">{{ formatCurrency(availableEquity - totalEquity) }}</h1>
        </div>
      </div>
      <div class="flex flex-row justify-center">
        <StackedLabelDropdownNoForm
          v-model="selectedProjectEntity"
          name="projectEntity"
          label="Project Entity"
          size="max-w-[28rem] w-full px-2 mt-3"
          :options="availableProjectEntities"
          :options-label="(projectEntity: ProjectEntity) => getEntityDisplay(projectEntity.entity)"
          :showClear="true"
          placeholder="Select a project entity"
        />
        <Button class="h-10 mt-auto" icon="pi pi-plus" label="Add" @click="addSplitPaymentEntity"></Button>
      </div>
    </template>
  </Card>
  <div class="h-[50vh] scroll-auto overflow-auto">
    <div v-for="(splitPayment, index) of splitPayments">
      <Card>
        <template #content>
          <div class="flex justify-between">
            <div class="flex flex-col gap-1 my-auto">
              <label class="font-bold text-xl">{{ getEntityDisplay(splitPayment.projectEntity.entity) }}</label>
              <label>{{ splitPayment.projectEntity.type }}</label>
            </div>

            <div class="flex justify-evenly gap-10">
              <div class="flex flex-col gap-1">
                <label class="font-bold">Loan Amount</label>
                <InputGroup>
                  <InputNumber
                    v-model="splitPayment.loanAmount"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    input-class="rounded-l-lg!"
                    :min="0.0"
                    :max="availableLoan - totalLoan + splitPayment.loanAmount"
                  ></InputNumber>
                </InputGroup>
              </div>
              <div class="flex flex-col gap-1">
                <label class="font-bold">Equity Amount</label>
                <InputGroup>
                  <InputNumber
                    v-model="splitPayment.equityAmount"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                    input-class="rounded-l-lg!"
                    :min="0.0"
                    :max="availableEquity - totalEquity + splitPayment.equityAmount"
                  ></InputNumber>
                </InputGroup>
              </div>
            </div>
          </div>
          <Button
            class="h-6 w-6 mt-auto rounded-full absolute top-3 right-3"
            icon="pi pi-times"
            outlined
            severity="danger"
            @click="() => tryRemoveSplitPayment(splitPayment)"
          ></Button>
        </template>
      </Card>
    </div>
  </div>

  <SubmitOptions class="mt-3" submitLabel="Create Payments" @cancelled="emit('cancelled')" @submitted="submitSplitPayments"></SubmitOptions>
</template>

<script setup lang="ts">
import { formatCurrency, getEntityDisplay } from "@/services/Utility";
import type { ProjectEntity } from "@/types/ProjectEntity";
import type { Project } from "@/types/Projects";
import { useConfirm } from "primevue/useconfirm";

interface Props {
  project: Project;
  projectEntities: ProjectEntity[];
  availableLoan?: number;
  availableEquity?: number;
}

const props = withDefaults(defineProps<Props>(), {
  availableLoan: 0,
  availableEquity: 0,
});

const emit = defineEmits<{
  cancelled: [];
  paid: [];
}>();

const totalLoan = computed<number>(() => splitPayments.value.reduce((acc, curr) => acc + curr.loanAmount, 0));
const totalEquity = computed<number>(() => splitPayments.value.reduce((acc, curr) => acc + curr.equityAmount, 0));

const selectedProjectEntity = ref<ProjectEntity>();

const sortAvailable = (): void => {
  availableProjectEntities.value.sort((a, b) => getEntityDisplay(a.entity).localeCompare(getEntityDisplay(b.entity)));
};

const availableProjectEntities = ref<ProjectEntity[]>(props.projectEntities);
sortAvailable();

type SplitPayment = {
  projectEntity: ProjectEntity;
  loanAmount: number;
  equityAmount: number;
};
const splitPayments = ref<SplitPayment[]>([]);

const addSplitPaymentEntity = (): void => {
  if (selectedProjectEntity.value == null) return;

  availableProjectEntities.value = availableProjectEntities.value.filter((pe) => pe.projectEntityId !== selectedProjectEntity.value?.projectEntityId);
  splitPayments.value.push({
    projectEntity: selectedProjectEntity.value,
    loanAmount: 0,
    equityAmount: 0,
  });
  selectedProjectEntity.value = undefined;
};

const confirm = useConfirm();

const tryRemoveSplitPayment = (splitPayment: SplitPayment): void => {
  if (splitPayment.equityAmount === 0 && splitPayment.loanAmount === 0) {
    removeSplitPayment(splitPayment.projectEntity);
  } else {
    confirmPopup(
      confirm,
      "Remove Project Entity?",
      "Do you want to remove " + getEntityDisplay(splitPayment.projectEntity.entity) + "?",
      () => removeSplitPayment(splitPayment.projectEntity),
      () => {}
    );
  }
};

const removeSplitPayment = (projectEntity: ProjectEntity): void => {
  splitPayments.value = splitPayments.value.filter((splitPayment) => splitPayment.projectEntity.projectEntityId !== projectEntity.projectEntityId);
  availableProjectEntities.value.push(projectEntity);
  sortAvailable();
};

const submitSplitPayments = (): void => {
  if (
    splitPayments.value.length == 0 ||
    splitPayments.value.filter((splitPayment) => splitPayment.equityAmount > 0 || splitPayment.loanAmount > 0).length == 0
  ) {
    error("Invalid Payments", "No valid payments defined");
    return;
  }

  axios
    .post("/ledger/splitPayment/" + props.project.projectInfo.projectId + "/" + props.project.projectDrawInfo.drawNumber, splitPayments.value)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          success("Success", "New payment(s) available to process");
          emit("paid");
        },
        (errors: ApiError[]) => {
          error("Error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};
</script>
